import { useEffect, useRef, useState } from 'react';
import { api, ApiRequestError, ASSET_SLOTS, type AssetSlot, type SiteAssets, type HomeAppearance } from '../api.ts';
import { Banner, Button, Card } from './ui.tsx';
import { siteAssetsChanged } from '../siteAssets.ts';
import { ImageCropper } from './ImageCropper.tsx';

/**
 * The admin's Appearance panel.
 *
 * The club's logo, the icon in the browser tab, the banner on the champion
 * screen. These used to be files in the repo, which meant changing the logo was
 * a rebuild and a redeploy — for a picture. Now they are rows in a table and an
 * upload, with the same re-encoding pipeline the profile pictures use, so an
 * admin swapping the logo cannot accidentally put a document on every page.
 */

const SLOT_COPY: Record<AssetSlot, { title: string; hint: string }> = {
  logo: {
    title: 'Logo',
    hint: 'Top of the home page.',
  },
  favicon: {
    title: 'Tab icon',
    hint: 'Browser tab icon.',
  },
  banner: {
    title: 'Champion banner',
    hint: 'Top of the champion screen.',
  },
  homePhoto1: { title: 'Home photo 1', hint: '' },
  homePhoto2: { title: 'Home photo 2', hint: '' },
  homePhoto3: { title: 'Home photo 3', hint: '' },
};

function AssetRow({
  slot,
  url,
  busy,
  onUpload,
  onClear,
}: {
  slot: AssetSlot;
  url: string | null;
  busy: boolean;
  onUpload: (file: File) => void;
  onClear: () => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const copy = SLOT_COPY[slot];

  return (
    <div className="flex flex-wrap items-center gap-3 border-b border-border py-3 last:border-b-0">
      <div className="flex size-12 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-border bg-surface">
        {url ? (
          <img src={url} alt="" className="size-full object-contain" />
        ) : (
          <span aria-hidden className="text-xs text-text-faint">
            none
          </span>
        )}
      </div>

      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium">{copy.title}</p>
        {copy.hint && <p className="text-xs text-text-faint">{copy.hint}</p>}
      </div>

      <Button variant="secondary" disabled={busy} onClick={() => fileRef.current?.click()}>
        {url ? 'Replace' : 'Upload'}
      </Button>
      {url && (
        <Button variant="ghost" disabled={busy} onClick={onClear}>
          Remove
        </Button>
      )}

      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg,image/gif,image/webp"
        className="hidden"
        aria-label={`Upload ${copy.title}`}
        data-asset={slot}
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = '';
          if (file) onUpload(file);
        }}
      />
    </div>
  );
}

export function Appearance() {
  const [assets, setAssets] = useState<SiteAssets | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [appearance, setAppearance] = useState<HomeAppearance | null>(null);
  const [crop, setCrop] = useState<{ slot: AssetSlot; file: File } | null>(null);

  useEffect(() => {
    void api
      .assets()
      .then(({ assets: current }) => setAssets(current))
      .catch(() => setError('Could not load the site assets.'));
    void api.appearance().then(({ appearance: current }) => setAppearance(current));
  }, []);

  async function run(work: () => Promise<{ assets: SiteAssets }>) {
    setBusy(true);
    setError(null);
    try {
      const { assets: next } = await work();
      setAssets(next);
      // So the tab icon and the logo change now, not on the next reload.
      siteAssetsChanged(next);
    } catch (caught) {
      setError(caught instanceof ApiRequestError ? caught.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  }

  async function saveAppearance(next: HomeAppearance) {
    setAppearance(next);
    try {
      const result = await api.saveAppearance(next);
      setAppearance(result.appearance);
    } catch (caught) {
      setError(caught instanceof ApiRequestError ? caught.message : 'Could not save the home layout.');
    }
  }

  return (
    <Card className="mb-6">
      {crop && <ImageCropper file={crop.file} aspect={crop.slot === 'favicon' ? 1 : crop.slot.startsWith('homePhoto') || crop.slot === 'banner' ? 3 : 2} title={`Frame ${SLOT_COPY[crop.slot].title.toLowerCase()}`} onCancel={() => setCrop(null)} onSave={(file) => { const slot = crop.slot; setCrop(null); void run(() => api.uploadAsset(slot, file)); }} />}
      <h2 className="mb-2 font-medium">Appearance</h2>

      {error && (
        <div className="my-2">
          <Banner tone="danger">{error}</Banner>
        </div>
      )}

      {appearance && (
        <div className="mt-4 border-t border-border pt-4">
          <label className="block text-sm font-medium" htmlFor="motd">Message of the day</label>
          <textarea id="motd" className="mt-1 min-h-20 w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm" maxLength={500} value={appearance.message} onChange={(event) => setAppearance({ ...appearance, message: event.target.value })} onBlur={() => void saveAppearance(appearance)} placeholder="Welcome to Friday night…" />
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <label className="text-xs text-text-faint">Font size
              <input className="mt-1 w-full" type="range" min="12" max="64" value={appearance.messageSize} onChange={(event) => setAppearance({ ...appearance, messageSize: Number(event.target.value) })} onMouseUp={() => void saveAppearance(appearance)} />
            </label>
            <label className="text-xs text-text-faint">Message position
              <select className="mt-1 w-full rounded-lg border border-border bg-surface p-2 text-sm text-text" value={appearance.messagePlacement} onChange={(event) => void saveAppearance({ ...appearance, messagePlacement: event.target.value as HomeAppearance['messagePlacement'] })}><option value="top">Top</option><option value="before_lobbies">Before lobbies</option><option value="after_lobbies">After lobbies</option></select>
            </label>
          </div>
          {(['homePhoto1', 'homePhoto2', 'homePhoto3'] as const).map((slot) => (
            <div key={slot} className="mt-3 grid gap-2 sm:grid-cols-3">
              <label className="text-xs text-text-faint">{SLOT_COPY[slot].title} position
                <select className="mt-1 w-full rounded-lg border border-border bg-surface p-2 text-sm text-text" value={appearance.photos[slot].placement} onChange={(event) => void saveAppearance({ ...appearance, photos: { ...appearance.photos, [slot]: { ...appearance.photos[slot], placement: event.target.value as HomeAppearance['messagePlacement'] } } })}><option value="top">Top</option><option value="before_lobbies">Before lobbies</option><option value="after_lobbies">After lobbies</option></select>
              </label>
              <label className="text-xs text-text-faint">Alignment
                <select className="mt-1 w-full rounded-lg border border-border bg-surface p-2 text-sm text-text" value={appearance.photos[slot].align} onChange={(event) => void saveAppearance({ ...appearance, photos: { ...appearance.photos, [slot]: { ...appearance.photos[slot], align: event.target.value as 'left' | 'center' | 'right' } } })}><option value="left">Left</option><option value="center">Center</option><option value="right">Right</option></select>
              </label>
              <label className="text-xs text-text-faint">Size · {appearance.photos[slot].size}%
                <input className="mt-2 w-full" type="range" min="20" max="100" step="5" value={appearance.photos[slot].size} onChange={(event) => setAppearance({ ...appearance, photos: { ...appearance.photos, [slot]: { ...appearance.photos[slot], size: Number(event.target.value) } } })} onPointerUp={() => void saveAppearance(appearance)} />
              </label>
            </div>
          ))}
        </div>
      )}

      {assets === null ? (
        <p className="py-3 text-sm text-text-faint">Loading…</p>
      ) : (
        <div>
          {ASSET_SLOTS.map((slot) => (
            <AssetRow
              key={slot}
              slot={slot}
              url={assets[slot]}
              busy={busy}
              onUpload={(file) => setCrop({ slot, file })}
              onClear={() => void run(() => api.clearAsset(slot))}
            />
          ))}
        </div>
      )}

    </Card>
  );
}
